import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import DataTable from '../../../components/ui/DataTable'
import KpiCards from '../../../components/ui/KpiCards'
import SectionCard from '../../../components/ui/SectionCard'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { personName, type AgendaParticipantResponse, type AgendaResponseSummary } from '../types'
import ResponseBadge from './ResponseBadge'

interface Props {
  responses: AgendaParticipantResponse[]
  summary?: AgendaResponseSummary
}

/**
 * Sahibe gösterilen yanıt dökümü: özet sayılar + katılımcı başına durum, mazeret ve yerine katılma bağı
 * (131). Veriyi süzmez — katılımcıya bu alanlar sunucudan hiç gelmez. Dar ekranda kart listesi.
 */
export default function ResponsesTable({ responses, summary }: Readonly<Props>) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  return (
    <SectionCard title={t('agenda.responses.title')} className="agenda-responses">
      {summary && (
        <KpiCards
          cards={[
            { key: 'attending', label: t('agenda.responses.attending'), value: String(summary.attending) },
            {
              key: 'notAttending',
              label: t('agenda.responses.notAttending'),
              value: String(summary.notAttending),
            },
            { key: 'pending', label: t('agenda.responses.pending'), value: String(summary.pending) },
          ]}
        />
      )}
      {isMobile ? (
        <ResponseCards responses={responses} t={t} />
      ) : (
        <DataTable value={responses} dataKey="employeeId" emptyMessage={t('agenda.responses.empty')}>
          <Column header={t('agenda.responses.name')} body={nameBody} />
          <Column
            header={t('agenda.responses.status')}
            body={(r: AgendaParticipantResponse) => <ResponseBadge response={r.response} />}
          />
          <Column
            header={t('agenda.responses.excuse')}
            body={(r: AgendaParticipantResponse) => <span className="agenda-excuse">{r.reason ?? ''}</span>}
          />
        </DataTable>
      )}
    </SectionCard>
  )
}

/** İsim hücresi: ad + yerine katılma bağı. Modül seviyesinde — bileşen içinde tanımlı JSX fonksiyonu her
 *  çizimde yeniden yaratılırdı (Sonar S6478). */
function nameBody(r: AgendaParticipantResponse) {
  return (
    <>
      {personName(r)}
      <SubstituteNote response={r} />
    </>
  )
}

/**
 * Yerine katılma bağı (131) — yalnız yerine katılanın satırında: "… yerine". Hem kişinin listede neden
 * olduğunu hem kimin yerine geldiğini söyler; katılmayanın satırında tekrarlanmaz (kullanıcı kararı
 * 03.11.2026). Bağ yoksa hiçbir şey çizilmez.
 */
function SubstituteNote({ response }: Readonly<{ response: AgendaParticipantResponse }>) {
  const { t } = useTranslation()
  if (!response.substituteFor) {
    return null
  }
  return (
    <div className="agenda-muted">
      {t('agenda.responses.substituteFor', { name: personName(response.substituteFor) })}
    </div>
  )
}

function ResponseCards({
  responses,
  t,
}: Readonly<{ responses: AgendaParticipantResponse[]; t: TFunction }>) {
  return (
    <div className="ui-card-list">
      {responses.map((r) => (
        <div key={r.employeeId} className="ui-card">
          <div className="ui-card-top">
            <span className="ui-card-title">{personName(r)}</span>
            <ResponseBadge response={r.response} />
          </div>
          <SubstituteNote response={r} />
          {r.reason && (
            <p className="agenda-excuse">
              <span className="agenda-muted">{t('agenda.responses.excuse')}: </span>
              {r.reason}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}
